import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause, FaExpand, FaVolumeUp, FaVolumeMute, FaVolumeDown } from "react-icons/fa";
import Hls from "hls.js";
import "./DarsVideoPlayer.css";

import CONFIG from "../config";

function DarsVideoPlayer({ src, onPlay, onPause, phoneNumber, mavzuId, videoId }) {


    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const hlsRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [maxReachedTime, setMaxReachedTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [watermarkPos, setWatermarkPos] = useState({ top: "10%", left: "10%" });

    const logIntervalRef = useRef(null);
    const lastLogTimeRef = useRef(Date.now());

    const logWatchingTime = async () => {
        if (!mavzuId || !videoId) return;
        const now = Date.now();
        const duration = Math.floor((now - lastLogTimeRef.current) / 1000);
        if (duration < 3) return; // Don't log if too short

        try {
            const token = localStorage.getItem("loyiha_token");
            await fetch(`${CONFIG.API_BASE_URL}/log_video_time.php?token=${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    video_id: videoId,
                    mavzu_id: mavzuId,
                    vaqt: duration
                })
            });
            lastLogTimeRef.current = now;
        } catch (err) {
            console.error("Video log error:", err);
        }
    };

    // Watermark random mover
    useEffect(() => {
        const interval = setInterval(() => {
            const top = Math.floor(Math.random() * 80) + 10 + "%";
            const left = Math.floor(Math.random() * 80) + 10 + "%";
            setWatermarkPos({ top, left });
        }, 10000);

        return () => {
            clearInterval(interval);
            if (logIntervalRef.current) clearInterval(logIntervalRef.current);
            logWatchingTime(); // Final log attempt
        };
    }, []);

    // Initialize Hls or Standard Video
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src) return;

        // Cleanup previous Hls instance
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        if (src.endsWith(".m3u8")) {
            if (Hls.isSupported()) {
                const hls = new Hls({
                    // HLS security/optimization options can be added here
                    xhrSetup: (xhr, url) => {
                        // This is where can add authorization headers in Phase 2
                        xhr.withCredentials = true;
                    }
                });
                hls.loadSource(src);
                hls.attachMedia(video);
                hlsRef.current = hls;

                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    // Ready to play
                });

                hls.on(Hls.Events.ERROR, (event, data) => {
                    if (data.fatal) {
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR:
                                hls.startLoad();
                                break;
                            case Hls.ErrorTypes.MEDIA_ERROR:
                                hls.recoverMediaError();
                                break;
                            default:
                                hls.destroy();
                                break;
                        }
                    }
                });
            } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
                // For Safari/iOS which has native HLS support
                video.src = src;
            }
        } else {
            // Standard MP4 Fallback
            video.src = src;
        }

        // Load saved time on mount or when src changes
        try {
            const savedTime = localStorage.getItem(`video-time-${src}`);
            const savedMax = localStorage.getItem(`video-max-${src}`);

            if (savedTime) {
                const time = parseFloat(savedTime);
                if (isFinite(time)) {
                    // Safety: wait for metadata? No, just try
                    try { video.currentTime = time; } catch (e) { console.warn("Seek error:", e); }
                    setMaxReachedTime(parseFloat(savedMax || savedTime));
                }
            } else {
                setMaxReachedTime(0);
                video.currentTime = 0;
            }
        } catch (e) {
            console.warn("Storage/Seek error:", e);
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [src]);

    if (!src) return <div className="video-error-placeholder" style={{ width: '100%', height: '100%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Video manbasi topilmadi</div>;


    const togglePlay = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (videoRef.current.paused) {
            videoRef.current.play();
            setIsPlaying(true);
        } else {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    };

    const toggleFullscreen = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!document.fullscreenElement) {
            if (containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen();
            } else if (containerRef.current.webkitRequestFullscreen) {
                containerRef.current.webkitRequestFullscreen();
            } else if (containerRef.current.msRequestFullscreen) {
                containerRef.current.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };

    const handleVolumeChange = (e) => {
        const value = parseFloat(e.target.value);
        setVolume(value);
        if (videoRef.current) {
            videoRef.current.volume = value;
            setIsMuted(value === 0);
        }
    };

    const toggleMute = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (videoRef.current) {
            const newMuted = !isMuted;
            setIsMuted(newMuted);
            videoRef.current.muted = newMuted;
            if (newMuted) {
                videoRef.current.volume = 0;
            } else {
                videoRef.current.volume = volume || 0.5;
                if (volume === 0) setVolume(0.5);
            }
        }
    };

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        const current = videoRef.current.currentTime;
        const total = videoRef.current.duration;

        localStorage.setItem(`video-time-${src}`, current);
        if (current > maxReachedTime) {
            setMaxReachedTime(current);
            localStorage.setItem(`video-max-${src}`, current);
        }

        setProgress((current / total) * 100);
    };

    const handleSeek = (e) => {
        const container = e.currentTarget;
        const rect = container.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        if (videoRef.current) {
            videoRef.current.currentTime = pos * duration;
        }
    };

    const handleLoadedMetadata = () => {
        setDuration(videoRef.current.duration);
    };

    const formatTime = (time) => {
        const min = Math.floor(time / 60);
        const sec = Math.floor(time % 60);
        return `${min}:${sec < 10 ? "0" : ""}${sec}`;
    };

    const preventAction = (e) => e.preventDefault();

    return (
        <div ref={containerRef} className="custom-player-container" onContextMenu={preventAction}>
            <video
                key={src || 'no-src'}
                ref={videoRef}
                className="custom-video-element"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onError={(e) => {
                    console.error("Video Playback Error:", e);
                    // Prevent crash by not doing anything drastic here
                }}
                onClick={togglePlay}
                onPlay={() => {
                    setIsPlaying(true);
                    lastLogTimeRef.current = Date.now();
                    logIntervalRef.current = setInterval(logWatchingTime, 15000);
                    if (onPlay) onPlay();
                }}
                onPause={() => {
                    setIsPlaying(false);
                    if (logIntervalRef.current) {
                        clearInterval(logIntervalRef.current);
                        logIntervalRef.current = null;
                    }
                    logWatchingTime();
                    if (onPause) onPause();
                }}
                preload="metadata"
                playsInline
            />

            <div className="custom-controls">
                <button type="button" className="play-pause-btn" onClick={togglePlay}>
                    {isPlaying ? <FaPause /> : <FaPlay />}
                </button>

                <div className="progress-container" onClick={handleSeek}>
                    <div
                        className="progress-bar"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                <div className="time-display">
                    {formatTime(videoRef.current?.currentTime || 0)} / {formatTime(duration)}
                </div>

                <div className="volume-control">
                    <button type="button" className="volume-btn" onClick={toggleMute}>
                        {isMuted || volume === 0 ? <FaVolumeMute /> : volume < 0.5 ? <FaVolumeDown /> : <FaVolumeUp />}
                    </button>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className="volume-slider"
                    />
                </div>

                <button type="button" className="fullscreen-btn" onClick={toggleFullscreen}>
                    <FaExpand />
                </button>
            </div>

            {/* Dynamic Watermark */}
            <div
                className="video-watermark"
                style={{ top: watermarkPos.top, left: watermarkPos.left }}
            >
                {localStorage.getItem("loyiha_user_name") || phoneNumber || localStorage.getItem("user_id") || "Secure Content"}
            </div>
        </div>
    );
}

export default DarsVideoPlayer;

